import { inject, Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { catchError, map, Observable, of } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import {SearchDictsService} from "./search-dicts.service";

/** Normalized item for list + fly-to (legacy Geocoder `features` or Search Box `suggestions`). */
export type MapboxResult = {
  id?: string;
  text?: string;
  place_name?: string;
  place_type?: string[];
  /** Mapbox order: [lng, lat] */
  center?: [number, number];
  /** From Search Box `suggest` — required for `/retrieve` when `center` is missing */
  sessionToken?: string;
  [key: string]: unknown;
};

/** Legacy Geocoder-style JSON */
type MapboxGeocoderResponse = {
  features?: MapboxResult[];
  query?: string[];
  type?: string;
};

/** Search Box / backend proxy: `suggestions` instead of `features`. */
type MapboxSearchBoxResponse = {
  suggestions?: MapboxSearchBoxSuggestion[];
  session_token?: string;
  response_id?: string;
  attribution?: string;
};

type MapboxSearchBoxSuggestion = Record<string, unknown>;
export type SearchResultIcon = 'boundary' | 'area' | 'location';
export type SearchResult<T> = {
  icon: SearchResultIcon;
  title: string;
  description: string;
  data: T;
};

export type SearchPointItem = {
  hint: string;
  label: string;
  type: 'coors';
  x: string;
  y: string;
};
export const isSearchPointItem = (
  data: SearchPointItem | SearchPlotItem | SearchOtherItem,
): data is SearchPointItem => {
  return data.type === 'coors';
};

export type SearchPlotItem = {
  bbox: unknown[];
  coordinates: number[][][];
  hint: string;
  label: string;
  type: 'plot';
  uuid: string;
};
export const isSearchPlotItem = (
  data: SearchPointItem | SearchPlotItem | SearchOtherItem,
): data is SearchPlotItem => {
  return data.type === 'plot';
};

export type SearchOtherItem = {
  hint: string;
  label: string;
  type: 'desa' | 'kecamatan' | 'kabupaten' | 'province';
  zoneId: string;
  zoneType: string;
};
export const isSearchOtherItem = (
  data: SearchPointItem | SearchPlotItem | SearchOtherItem,
): data is SearchOtherItem => {
  const possibleTypes = ['desa', 'kecamatan', 'kabupaten', 'desa', 'province'];
  return possibleTypes.includes(data.type);
};

export type SearchApiItem = SearchPointItem | SearchPlotItem | SearchOtherItem;

type SearchApiResponse = {
  list: SearchApiItem[];
};

@Injectable({
  providedIn: 'root',
})
export class SearchService {
  private http = inject(HttpClient);
  private searchDicts = inject(SearchDictsService);

  public searchByString(searchStr: string): Observable<SearchResult<SearchApiItem>[]> {
    return this.http
      .post<SearchApiResponse>(`${environment.apiUrl}/api/search`, { search: searchStr })
      .pipe(
        map((data: SearchApiResponse) => {
          return data.list
            .map(
              (
                item: SearchPlotItem | SearchPointItem | SearchOtherItem,
              ): SearchResult<SearchApiItem> | null => {
                if (isSearchPointItem(item)) {
                  return {
                    icon: 'location',
                    title: item.label,
                    description: item.hint,
                    data: item,
                  };
                } else if (isSearchPlotItem(item)) {
                  return {
                    icon: 'area',
                    title: item.label,
                    description: item.hint,
                    data: item,
                  };
                } else if (isSearchOtherItem(item)) {
                  return {
                    icon: 'boundary',
                    title: item.label,
                    description: item.hint,
                    data: item,
                  };
                }

                return null;
              },
            )
            .filter((it: SearchResult<SearchApiItem> | null): it is SearchResult<SearchApiItem> => {
              return it !== null && it !== undefined;
            });
        }),
      );
  }

  public searchByNib(
    searchStr: string,
    searchNibData: { desa: string; kabupaten: string; ownershipType: string },
  ): Observable<SearchResult<SearchApiItem>[]> {
    return this.http
      .post<SearchApiResponse>(`${environment.apiUrl}/api/search`, {
        search: searchStr,
        ...searchNibData,
      })
      .pipe(
        map((data: SearchApiResponse) => {
          return data.list
            .map(
              (
                item: SearchPlotItem | SearchPointItem | SearchOtherItem,
              ): SearchResult<SearchApiItem> | null => {
                if (isSearchPointItem(item)) {
                  return {
                    icon: 'location',
                    title: item.label,
                    description: item.hint,
                    data: item,
                  };
                } else if (isSearchPlotItem(item)) {
                  return {
                    icon: 'area',
                    title: item.label,
                    description: item.hint,
                    data: item,
                  };
                } else if (isSearchOtherItem(item)) {
                  return {
                    icon: 'boundary',
                    title: item.label,
                    description: item.hint,
                    data: item,
                  };
                }

                return null;
              },
            )
            .filter((it: SearchResult<SearchApiItem> | null): it is SearchResult<SearchApiItem> => {
              return it !== null && it !== undefined;
            });
        }),
      );
  }

  searchByMapbox(term: string, lngLat: { lng: string; lat: string }): Observable<MapboxResult[]> {
    return this.http
      .post<MapboxGeocoderResponse & MapboxSearchBoxResponse>(`${environment.apiUrl}/api/search/mapbox`, {
        search: term,
        latitude: lngLat.lat,
        longitude: lngLat.lng,
      })
      .pipe(map((it) => normalizeMapboxSearchResponse(it)));
  }

  /**
   * Search Box second step: get coordinates for a suggestion (when suggest payload has no lat/lng).
   * @see https://docs.mapbox.com/api/search/search-box/#retrieve-a-suggested-feature
   */
  retrieveMapboxSuggestionLngLat(mapboxId: string, sessionToken: string): Observable<[number, number] | null> {
    const url = `https://api.mapbox.com/search/searchbox/v1/retrieve/${encodeURIComponent(mapboxId)}`;
    return this.http
      .get<MapboxRetrieveFeatureCollection>(url, {
        params: {
          session_token: sessionToken,
          access_token: environment.mapboxToken,
        },
      })
      .pipe(
        map((fc) => lngLatFromRetrieveResponse(fc)),
        catchError(() => of(null)),
      );
  }
}

type MapboxRetrieveFeatureCollection = {
  type?: string;
  bbox?: [number, number, number, number];
  features?: MapboxRetrieveFeature[];
};

type MapboxRetrieveFeature = {
  bbox?: [number, number, number, number];
  geometry?: {
    type?: string;
    coordinates?: unknown;
  };
};

function lngLatFromRetrieveResponse(fc: MapboxRetrieveFeatureCollection): [number, number] | null {
  const collBbox = fc.bbox;
  if (Array.isArray(collBbox) && collBbox.length >= 4) {
    const c = bboxToCenter(collBbox);
    if (c) {
      return c;
    }
  }
  const f = fc.features?.[0];
  if (!f) {
    return null;
  }
  if (Array.isArray(f.bbox) && f.bbox.length >= 4) {
    const c = bboxToCenter(f.bbox);
    if (c) {
      return c;
    }
  }
  const g = f.geometry;
  if (!g?.type) {
    return null;
  }
  if (g.type === 'Point') {
    const c = g.coordinates as [number, number];
    if (
      Array.isArray(c) &&
      c.length >= 2 &&
      Number.isFinite(c[0]) &&
      Number.isFinite(c[1])
    ) {
      return [c[0], c[1]];
    }
  }
  if (g.type === 'Polygon') {
    const ring = (g.coordinates as [number, number][][])?.[0];
    return polygonRingCentroid(ring);
  }
  if (g.type === 'MultiPolygon') {
    const ring = (g.coordinates as [number, number][][][])?.[0]?.[0];
    return polygonRingCentroid(ring);
  }
  return null;
}

function polygonRingCentroid(ring: [number, number][] | undefined): [number, number] | null {
  if (!Array.isArray(ring) || ring.length === 0) {
    return null;
  }
  let sumLng = 0;
  let sumLat = 0;
  let n = 0;
  for (const pt of ring) {
    if (
      Array.isArray(pt) &&
      pt.length >= 2 &&
      Number.isFinite(pt[0]) &&
      Number.isFinite(pt[1])
    ) {
      sumLng += pt[0];
      sumLat += pt[1];
      n++;
    }
  }
  if (!n) {
    return null;
  }
  return [sumLng / n, sumLat / n];
}

function bboxToCenter(bbox: [number, number, number, number]): [number, number] | null {
  const [w, s, e, n] = bbox;
  if (![w, s, e, n].every((x) => typeof x === 'number' && Number.isFinite(x))) {
    return null;
  }
  return [(w + e) / 2, (s + n) / 2];
}

function normalizeMapboxSearchResponse(
  it: MapboxGeocoderResponse & MapboxSearchBoxResponse,
): MapboxResult[] {
  if (!it || typeof it !== 'object') {
    return [];
  }
  if (Array.isArray(it.features) && it.features.length) {
    return it.features as MapboxResult[];
  }
  if (Array.isArray(it.suggestions) && it.suggestions.length) {
    const sessionToken =
      typeof it['session_token'] === 'string' ? it['session_token'] : undefined;
    return it.suggestions.map((sug) =>
      mapSearchBoxSuggestionToMapboxResult(sug, sessionToken),
    );
  }
  return [];
}

function mapSearchBoxSuggestionToMapboxResult(
  s: MapboxSearchBoxSuggestion,
  sessionToken?: string,
): MapboxResult {
  const name = String(s['name'] ?? '');
  const placeFormatted = String(s['place_formatted'] ?? '');
  const fullAddress = String(s['full_address'] ?? '');
  const subtitle = fullAddress || placeFormatted;
  const featureType = String(s['feature_type'] ?? '');

  const center = extractLngLatFromMapboxSuggestion(s);

  return {
    id: (s['mapbox_id'] as string | undefined) ?? (s['id'] as string | undefined),
    text: name,
    place_name: subtitle || name,
    place_type: featureType ? [featureType] : [],
    center,
    sessionToken,
    _searchBoxSuggestion: s,
  };
}

function extractLngLatFromMapboxSuggestion(s: MapboxSearchBoxSuggestion): [number, number] | undefined {
  if (typeof s['longitude'] === 'number' && typeof s['latitude'] === 'number') {
    return [s['longitude'], s['latitude']];
  }
  if (typeof s['longitude'] === 'string' && typeof s['latitude'] === 'string') {
    const ln = parseFloat(s['longitude']);
    const la = parseFloat(s['latitude']);
    if (Number.isFinite(ln) && Number.isFinite(la)) {
      return [ln, la];
    }
  }

  const coords = s['coordinates'] as Record<string, unknown> | undefined;
  if (coords) {
    const lat = coords['latitude'] ?? coords['lat'];
    const lng = coords['longitude'] ?? coords['lng'];
    if (typeof lat === 'number' && typeof lng === 'number') {
      return [lng, lat];
    }
    if (typeof lat === 'string' && typeof lng === 'string') {
      const la = parseFloat(lat);
      const ln = parseFloat(lng);
      if (Number.isFinite(la) && Number.isFinite(ln)) {
        return [ln, la];
      }
    }
  }

  const geom = s['geometry'] as { coordinates?: [number, number] } | undefined;
  if (geom?.coordinates && geom.coordinates.length >= 2) {
    const [a, b] = geom.coordinates;
    if (Number.isFinite(a) && Number.isFinite(b)) {
      return [a, b];
    }
  }

  const c = s['center'] as [number, number] | undefined;
  if (Array.isArray(c) && c.length >= 2 && Number.isFinite(c[0]) && Number.isFinite(c[1])) {
    return [c[0], c[1]];
  }

  const bbox = s['bbox'] as [number, number, number, number] | undefined;
  if (
    Array.isArray(bbox) &&
    bbox.length === 4 &&
    bbox.every((n) => typeof n === 'number' && Number.isFinite(n))
  ) {
    const [w, sLat, e, nLat] = bbox;
    return [(w + e) / 2, (sLat + nLat) / 2];
  }

  return undefined;
}
